import React, { useState, useEffect } from 'react';
import { AlertTriangle, X, ExternalLink } from 'lucide-react';

export default function ExternalLinkWarning() {
  const [targetUrl, setTargetUrl] = useState(null);

  useEffect(() => {
    const handleGlobalClick = (e) => {
      // Find closest anchor tag
      const anchor = e.target.closest('a');
      if (!anchor) return;

      const href = anchor.getAttribute('href');
      // If it's an external link and not starting with / or # or mailto: or tel:
      if (href && href.startsWith('http')) {
        try {
          const url = new URL(href, window.location.href);
          // If the domain is different from current domain
          if (url.hostname !== window.location.hostname) {
            e.preventDefault();
            setTargetUrl(href);
          }
        } catch (err) {
          // invalid url, ignore
        }
      }
    };

    document.addEventListener('click', handleGlobalClick, true); // useCapture to catch it early
    return () => document.removeEventListener('click', handleGlobalClick, true);
  }, []);

  const handleProceed = () => {
    if (targetUrl) {
      window.open(targetUrl, '_blank', 'noopener,noreferrer');
      setTargetUrl(null);
    }
  };

  if (!targetUrl) return null;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2 text-[#d97706] font-semibold">
            <AlertTriangle className="w-5 h-5" />
            <h2>Leaving CISH Portal</h2>
          </div>
          <button onClick={() => setTargetUrl(null)} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-4 md:p-6 space-y-4">
          <p className="text-sm text-gray-700">
            You are about to navigate to an external website. Please confirm your destination:
          </p>
          
          <div className="bg-[#fffbeb] border border-[#fde68a] rounded p-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="overflow-hidden">
              <h3 className="font-semibold text-gray-900 text-sm mb-1">External Link</h3>
              <p className="text-xs text-[#92400e] font-mono break-all">{targetUrl}</p>
            </div>
            <button 
              onClick={handleProceed}
              className="shrink-0 flex items-center justify-center gap-2 bg-[#16a34a] hover:bg-[#15803d] text-white px-4 py-2 rounded text-sm font-medium transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              Proceed
            </button>
          </div>
          
          <p className="text-xs text-gray-500 mt-4">
            These links will open in a new tab. The CISH Portal is not responsible for the content, security, or privacy practices of external websites. This notice is displayed as per GIGW 3.0 compliance guidelines.
          </p>
        </div>
        
        <div className="p-4 border-t bg-gray-50 flex justify-center">
          <button 
            onClick={() => setTargetUrl(null)}
            className="w-full max-w-[200px] bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded font-medium transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
